import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { stringifyVariables } from 'urql';

import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../filters/hooks/usePrimaryFilterState';
import { useStableReference } from '../DataProvider/utils/use-stable-reference';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import { usePageState } from '../PageState/PageStateProvider';
import {
  CompositionV4QueryRunner,
  TopCompaniesQueryRunner,
} from './CompositionV4QueryRunner';
import {
  type DataResolverPlan,
  EMPTY_PLAN,
  planDataQueries,
} from './plan-data-queries';
import {
  isSameResult,
  type QueryResult,
  type QueryResults,
  type ResolvedItems,
  resolveItems,
} from './resolve-items';

export type SetQueryResult = (
  queryKey: string,
  result: QueryResult | null | undefined
) => void;

const DataResolverContext = createContext<ResolvedItems>({});

export const useResolvedItems = (): ResolvedItems =>
  useContext(DataResolverContext);

const arePlansEqual = (
  previous: DataResolverPlan,
  next: DataResolverPlan
): boolean =>
  previous === next ||
  stringifyVariables(previous) === stringifyVariables(next);

export const DataResolver = ({
  items,
  children,
}: {
  items: readonly PositionedItem[];
  children: ReactNode;
}) => {
  const isCompositionV4Enabled = useUnleashFlag(FeatureFlag.CompositionV4);
  const { primaryFilter } = useTabContentConfig();
  const { activePrimaryFilterLength } = usePrimaryFilterState(
    primaryFilter?.id
  );
  const filters = useEffectiveFilters({ viewGranularity: 'month' });
  const { geographySelectionListIds } = useFilterContext();
  const { pageState } = usePageState();
  const { exchangeRate } = useCurrencyPreference();

  const plan = useStableReference(
    isCompositionV4Enabled
      ? planDataQueries({
          items,
          primaryFilterId: primaryFilter?.id,
          entityCount: activePrimaryFilterLength,
          filters,
          geographySelectionListIds,
          primaryEntities: pageState.primaryEntities,
        })
      : EMPTY_PLAN,
    arePlansEqual
  );

  const [results, setResults] = useState<QueryResults>({});
  const setQueryResult = useCallback<SetQueryResult>((queryKey, result) => {
    setResults((current) => {
      if (isSameResult(current[queryKey], result)) return current;
      const { [queryKey]: _cleared, ...rest } = current;
      return result === undefined ? rest : { ...rest, [queryKey]: result };
    });
  }, []);

  const resolvedItems = useMemo(
    () => resolveItems(plan, results, exchangeRate),
    [plan, results, exchangeRate]
  );

  return (
    <DataResolverContext.Provider value={resolvedItems}>
      {Object.values(plan.queries).map((query) =>
        query.topCompaniesOf ? (
          <TopCompaniesQueryRunner
            key={query.key}
            query={query}
            topCompaniesOf={query.topCompaniesOf}
            setQueryResult={setQueryResult}
          />
        ) : (
          <CompositionV4QueryRunner
            key={query.key}
            query={query}
            setQueryResult={setQueryResult}
          />
        )
      )}
      {children}
    </DataResolverContext.Provider>
  );
};
